// frontend/src/components/BellCurveChart.jsx
// Bell-curve visualization.
// When `values` is provided, draws a Kernel Density Estimate from the player's
// actual game data. Otherwise falls back to a theoretical normal distribution.
import { useMemo } from 'react';

// ─── Math helpers ───────────────────────────────────────────────────────────

/** Standard normal PDF */
function stdNormalPDF(z) {
  return Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
}

/** Approximate CDF of the standard normal (Abramowitz & Stegun) */
function stdNormalCDF(z) {
  if (z < -8) return 0;
  if (z > 8) return 1;
  const a1=0.254829592, a2=-0.284496736, a3=1.421413741,
        a4=-1.453152027, a5=1.061405429, p=0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - ((((a5*t+a4)*t+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
  return 0.5 * (1.0 + sign * y);
}

/**
 * Gaussian kernel for KDE.
 * bandwidth h chosen via Silverman's rule: h = 1.06 * σ * n^(-1/5)
 */
function gaussianKernel(u) {
  return Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);
}

function silvermanBandwidth(values, stdDev) {
  const n = values.length;
  if (n < 2 || stdDev === 0) return 1;
  return 1.06 * stdDev * Math.pow(n, -0.2);
}

/**
 * Build KDE curve points from raw game values.
 * Returns [{x, y}] over the range [xMin, xMax].
 */
function buildKDE(values, mean, stdDev, steps = 200) {
  const safeSd = stdDev > 0 ? stdDev : 1;
  const h = silvermanBandwidth(values, safeSd);
  const n = values.length;

  // Extend ±3.5 SD around mean but floor at 0 for non-negative stats
  const xMin = Math.max(0, mean - 3.5 * safeSd);
  const xMax = mean + 3.5 * safeSd;

  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const x = xMin + ((xMax - xMin) * i) / steps;
    const density = values.reduce((sum, xi) => sum + gaussianKernel((x - xi) / h), 0) / (n * h);
    pts.push({ x, density });
  }
  return { pts, xMin, xMax };
}

/**
 * Approximate percentile from empirical values.
 * (Fraction of values <= playerValue)
 */
function empiricalPercentile(values, playerValue) {
  const below = values.filter((v) => v <= playerValue).length;
  return Math.round((below / values.length) * 100);
}

// ─── Component ──────────────────────────────────────────────────────────────

const BellCurveChart = ({
  mean,           // distribution mean
  stdDev,         // distribution standard deviation
  playerValue,    // the value to mark on the curve
  values,         // optional: raw game values for empirical KDE
  label,          // e.g. "vs Own Season"
  gradientId,     // unique SVG gradient ID
  accentColor = '#f97316',
  height = 210,
  className = '',
}) => {
  const gId = gradientId || `fill-${(label||'chart').replace(/[^a-zA-Z0-9]/g,'')}`;

  const WIDTH = 500;
  const HEIGHT = height;
  const PAD_X = 50;
  const PAD_TOP = 28;
  const PAD_BOT = 52;
  const PLOT_W = WIDTH - PAD_X * 2;
  const PLOT_H = HEIGHT - PAD_TOP - PAD_BOT;

  const safeSd = stdDev > 0 ? stdDev : 1;
  const useEmpirical = values && values.length >= 3;

  // ── Build curve data ────────────────────────────────────────────────────
  const curveData = useMemo(() => {
    if (useEmpirical) {
      return buildKDE(values, mean, safeSd);
    }
    // Fallback: theoretical normal over ±3.5 SD
    const xMin = mean - 3.5 * safeSd;
    const xMax = mean + 3.5 * safeSd;
    const pts = [];
    for (let i = 0; i <= 200; i++) {
      const x = xMin + ((xMax - xMin) * i) / 200;
      const z = (x - mean) / safeSd;
      pts.push({ x, density: stdNormalPDF(z) / safeSd });
    }
    return { pts, xMin, xMax };
  }, [useEmpirical, values, mean, safeSd]); // eslint-disable-line react-hooks/exhaustive-deps

  const { pts, xMin, xMax } = curveData;
  const maxDensity = Math.max(...pts.map((p) => p.density)) || 1;

  // ── Coordinate mappers ───────────────────────────────────────────────────
  const xToSVG = (x) => PAD_X + ((x - xMin) / (xMax - xMin)) * PLOT_W;
  const yToSVG = (d) => PAD_TOP + PLOT_H - (d / maxDensity) * PLOT_H;
  const baselineY = yToSVG(0);

  // ── Percentile ───────────────────────────────────────────────────────────
  const percentile = useEmpirical
    ? empiricalPercentile(values, playerValue)
    : Math.round(stdNormalCDF((playerValue - mean) / safeSd) * 100);

  // ── SVG paths ────────────────────────────────────────────────────────────
  const curvePath = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xToSVG(p.x).toFixed(2)},${yToSVG(p.density).toFixed(2)}`)
    .join(' ');

  const clampedX = Math.max(xMin, Math.min(xMax, playerValue));
  const fillPts = pts.filter((p) => p.x <= clampedX);
  const fillPath =
    fillPts.length > 1
      ? fillPts
          .map((p, i) => `${i === 0 ? 'M' : 'L'}${xToSVG(p.x).toFixed(2)},${yToSVG(p.density).toFixed(2)}`)
          .join(' ') +
        ` L${xToSVG(clampedX).toFixed(2)},${baselineY.toFixed(2)} L${xToSVG(xMin).toFixed(2)},${baselineY.toFixed(2)} Z`
      : '';

  // ── Player marker ────────────────────────────────────────────────────────
  const playerX = xToSVG(clampedX);
  // y at player x: interpolate density from nearest KDE point
  const nearestPt = pts.reduce((best, p) =>
    Math.abs(p.x - clampedX) < Math.abs(best.x - clampedX) ? p : best, pts[0]);
  const playerY = yToSVG(nearestPt?.density ?? 0);

  // ── X-axis ticks: μ and ±1σ, ±2σ, ±3σ ──────────────────────────────────
  const sdTicks = [-3, -2, -1, 0, 1, 2, 3]
    .map((sd) => ({ sd, x: mean + sd * safeSd }))
    .filter(({ x }) => x >= xMin && x <= xMax);

  return (
    <div className={className}>
      {/* Label row */}
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-sm font-semibold text-gray-300">{label}</span>
        <span
          className="text-sm font-bold px-2 py-0.5 rounded"
          style={{ color: accentColor, backgroundColor: `${accentColor}22` }}
        >
          {percentile}th percentile
        </span>
      </div>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        style={{ maxHeight: HEIGHT }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accentColor} stopOpacity="0.4" />
            <stop offset="100%" stopColor={accentColor} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Shaded fill up to player value */}
        {fillPath && <path d={fillPath} fill={`url(#${gId})`} />}

        {/* Bell / KDE curve */}
        <path d={curvePath} fill="none" stroke="#6b7280" strokeWidth="2" />

        {/* Baseline */}
        <line x1={PAD_X} y1={baselineY} x2={WIDTH - PAD_X} y2={baselineY} stroke="#4b5563" strokeWidth="1" />

        {/* SD ticks + labels */}
        {sdTicks.map(({ sd, x }) => {
          const svgX = xToSVG(x);
          return (
            <g key={sd}>
              <line x1={svgX} y1={baselineY} x2={svgX} y2={baselineY + 6} stroke="#6b7280" strokeWidth="1" />
              <text x={svgX} y={baselineY + 18} textAnchor="middle" fill="#9ca3af" fontSize="10">
                {sd === 0 ? 'μ' : `${sd > 0 ? '+' : ''}${sd}σ`}
              </text>
              <text x={svgX} y={baselineY + 32} textAnchor="middle" fill="#d1d5db" fontSize="11" fontWeight="600">
                {x < 0 ? '0' : Number.isInteger(x) ? x : x.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Player marker line */}
        <line
          x1={playerX} y1={playerY} x2={playerX} y2={baselineY}
          stroke={accentColor} strokeWidth="2" strokeDasharray="4 3"
        />

        {/* Player dot */}
        <circle cx={playerX} cy={playerY} r="5" fill={accentColor} />
        <circle cx={playerX} cy={playerY} r="8" fill="none" stroke={accentColor} strokeWidth="1.5" opacity="0.5" />

        {/* Player value label */}
        <text x={playerX} y={PAD_TOP - 6} textAnchor="middle" fill={accentColor} fontSize="13" fontWeight="700">
          {typeof playerValue === 'number' ? playerValue.toFixed(1) : playerValue}
        </text>
      </svg>
    </div>
  );
};

export default BellCurveChart;
